import React, { useEffect, useRef, useState } from 'react';
import {
  Plus,
  User,
  Package,
  Briefcase,
  Sparkles,
  ArrowDown,
  ChevronDown,
} from 'lucide-react';
import { HERO_PROMPTS, HERO_SUBTITLE } from '../mock';

/**
 * Hero — Claude-chat-style centerpiece, bespoke to Sirin.
 * Live flow on mount / chip click:
 *   1. typing   — question types into the input
 *   2. thinking — wave button pulses, response shows animated dots
 *   3. revealed — response fades in
 */

const CHIP_ICONS = [User, Package, Briefcase, Sparkles];

export default function Hero() {
  const [activeIdx, setActiveIdx] = useState(2);
  const [typedQ, setTypedQ] = useState('');
  const [phase, setPhase] = useState('typing'); // typing | thinking | revealed

  const typeTimer = useRef(null);
  const phaseTimer = useRef(null);
  const active = HERO_PROMPTS[activeIdx];

  // Listen for deep-link from the command palette
  useEffect(() => {
    const handler = (e) => {
      const idx = e?.detail?.index;
      if (typeof idx === 'number' && idx >= 0 && idx < HERO_PROMPTS.length) {
        setActiveIdx(idx);
      }
    };
    window.addEventListener('sirin:hero-prompt', handler);
    return () => window.removeEventListener('sirin:hero-prompt', handler);
  }, []);

  // Drive the typing -> thinking -> revealed sequence whenever activeIdx changes.
  useEffect(() => {
    clearTimeout(typeTimer.current);
    clearTimeout(phaseTimer.current);

    setTypedQ('');
    setPhase('typing');

    const target = active.q;
    let i = 0;

    const step = () => {
      i += 1;
      setTypedQ(target.slice(0, i));
      if (i < target.length) {
        // Slight natural variance; slow briefly after punctuation.
        const last = target[i - 1];
        const base = 20 + Math.random() * 18;
        const delay = last === ',' ? base + 70 : base;
        typeTimer.current = setTimeout(step, delay);
      } else {
        // Brief beat, then "thinking"
        phaseTimer.current = setTimeout(() => {
          setPhase('thinking');
          phaseTimer.current = setTimeout(() => {
            setPhase('revealed');
          }, 820);
        }, 280);
      }
    };
    typeTimer.current = setTimeout(step, 120);

    return () => {
      clearTimeout(typeTimer.current);
      clearTimeout(phaseTimer.current);
    };
  }, [activeIdx, active.q]);

  const handleSelect = (i) => {
    if (i === activeIdx) return;
    setActiveIdx(i);
  };

  const showCaret = phase === 'typing' || phase === 'thinking';
  const pulseVoice = phase === 'thinking';

  return (
    <section id="top" className="relative pt-28 md:pt-32 pb-20 md:pb-24">
      <div className="max-w-[880px] mx-auto px-5 md:px-6">
        {/* Top pill */}
        <div className="flex justify-center mb-14 md:mb-16 fade-up">
          <div
            className="inline-flex items-center gap-2 mono text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 rounded-full"
            style={{ background: 'var(--bone-deep)', color: 'var(--moss)' }}
          >
            <span
              className="inline-block w-1.5 h-1.5 rounded-full"
              style={{ background: '#3ea26a', boxShadow: '0 0 0 3px rgba(62,162,106,0.18)' }}
              aria-hidden
            />
            heysirin · portfolio
          </div>
        </div>

        {/* Greeting + mark + subtitle */}
        <div
          className="flex flex-col items-center text-center mb-12 md:mb-14 fade-up"
          style={{ animationDelay: '0.08s' }}
        >
          <div className="flex items-center justify-center gap-4 md:gap-5">
            <SirinMark />
            <h1
              className="display"
              style={{
                fontSize: 'clamp(2rem, 5vw, 3.5rem)',
                lineHeight: 1.02,
                fontWeight: 400,
                color: 'var(--ink)',
                letterSpacing: '-0.025em',
              }}
            >
              Good morning, I’m{' '}
              <em
                className="serif"
                style={{ fontStyle: 'italic', color: 'var(--forest)', fontWeight: 400 }}
              >
                Sirin
              </em>
              .
            </h1>
          </div>
          <p
            className="serif mt-5 max-w-[560px]"
            style={{
              fontSize: 'clamp(0.95rem, 1.2vw, 1.1rem)',
              lineHeight: 1.4,
              color: 'var(--moss)',
              fontStyle: 'italic',
              fontWeight: 400,
            }}
          >
            {HERO_SUBTITLE}
          </p>
        </div>

        {/* Input box */}
        <div className="fade-up" style={{ animationDelay: '0.16s' }}>
          <div
            className="rounded-[28px] p-5 md:p-6"
            style={{
              background: 'var(--cream)',
              border: '1px solid rgba(15,27,20,0.08)',
              boxShadow:
                '0 1px 0 rgba(15,27,20,0.03), 0 2px 6px -2px rgba(15,27,20,0.06), 0 24px 60px -32px rgba(15,27,20,0.18)',
            }}
          >
            <div className="min-h-[52px] md:min-h-[60px] flex items-start">
              <div
                className="text-[17px] md:text-[20px] leading-[1.5]"
                style={{ color: 'var(--ink)', letterSpacing: '-0.005em' }}
                aria-live="polite"
              >
                {typedQ || <span style={{ opacity: 0 }}> </span>}
                {showCaret && (
                  <span
                    className="cursor-blink ml-0.5"
                    style={{ color: 'var(--terracotta)' }}
                    aria-hidden
                  >
                    ▌
                  </span>
                )}
              </div>
            </div>

            <div className="mt-5 flex items-center justify-between">
              <button
                type="button"
                aria-label="Attach"
                className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                style={{
                  border: '1px solid rgba(15,27,20,0.14)',
                  color: 'var(--moss)',
                  background: 'transparent',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(15,27,20,0.04)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <Plus size={15} strokeWidth={1.75} />
              </button>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 mono text-[11px]"
                  style={{ color: 'var(--moss)' }}
                >
                  <span className="lowercase">sirin · v1.0</span>
                  <ChevronDown size={12} strokeWidth={1.75} />
                </button>
                <div
                  className={`w-9 h-9 rounded-full flex items-center justify-center ${
                    pulseVoice ? 'pulse-ring' : ''
                  }`}
                  style={{
                    background: 'var(--forest)',
                    color: 'var(--cream)',
                    transition: 'transform 0.3s ease',
                  }}
                  aria-hidden
                >
                  <WaveIndicator active={pulseVoice} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Chips */}
        <div
          className="mt-5 flex flex-wrap gap-2 justify-center fade-up"
          style={{ animationDelay: '0.24s' }}
        >
          {HERO_PROMPTS.map((p, i) => {
            const Icon = CHIP_ICONS[i];
            const isActive = i === activeIdx;
            return (
              <button
                key={p.chip}
                onClick={() => handleSelect(i)}
                className="inline-flex items-center gap-2 px-3.5 py-2.5 rounded-xl"
                style={{
                  background: isActive ? 'var(--forest)' : 'var(--cream)',
                  color: isActive ? 'var(--cream)' : 'var(--ink)',
                  border: `1px solid ${
                    isActive ? 'var(--forest)' : 'rgba(15,27,20,0.1)'
                  }`,
                  boxShadow: isActive ? 'none' : '0 1px 0 rgba(15,27,20,0.02)',
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.borderColor = 'rgba(15,27,20,0.25)';
                    e.currentTarget.style.background = 'var(--bone-soft)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.borderColor = 'rgba(15,27,20,0.1)';
                    e.currentTarget.style.background = 'var(--cream)';
                  }
                }}
                aria-pressed={isActive}
              >
                <Icon size={14} strokeWidth={1.75} />
                <span className="text-[13px]" style={{ letterSpacing: '-0.005em' }}>
                  {p.chip}
                </span>
              </button>
            );
          })}
        </div>

        {/* Response area — avatar is always there; content depends on phase */}
        <div className="mt-14 md:mt-16">
          <div className="flex items-start gap-4 md:gap-5">
            <div
              className="shrink-0 w-10 h-10 md:w-11 md:h-11 rounded-full flex items-center justify-center mt-1"
              style={{
                background: 'var(--forest)',
                color: 'var(--cream)',
                boxShadow: '0 10px 24px -12px rgba(31,59,45,0.45)',
                transition: 'opacity 0.3s ease',
                opacity: phase === 'typing' ? 0.35 : 1,
              }}
              aria-hidden
            >
              <span
                className="serif"
                style={{
                  fontStyle: 'italic',
                  fontWeight: 500,
                  fontSize: '18px',
                  lineHeight: 1,
                  marginTop: 1,
                }}
              >
                s
              </span>
            </div>

            <div className="flex-1 min-w-0">
              <div
                className="mono text-[10px] uppercase tracking-[0.22em] mb-3 flex items-center gap-2"
                style={{ color: 'var(--stone)', minHeight: 14 }}
              >
                {phase === 'typing' && <span style={{ opacity: 0 }}>.</span>}
                {phase === 'thinking' && (
                  <>
                    <span>Sirin</span>
                    <span style={{ color: 'rgba(15,27,20,0.2)' }}>·</span>
                    <span style={{ color: 'var(--forest)' }}>typing</span>
                    <ThinkingDots />
                  </>
                )}
                {phase === 'revealed' && (
                  <>
                    <span>Sirin</span>
                    <span style={{ color: 'rgba(15,27,20,0.2)' }}>·</span>
                    <span>just now</span>
                  </>
                )}
              </div>

              {/* Answer: rendered only when revealed, with a fresh fade-up key */}
              {phase === 'revealed' && (
                <p
                  key={`a-${activeIdx}`}
                  className="serif fade-up"
                  style={{
                    fontSize: 'clamp(1.2rem, 2vw, 1.6rem)',
                    lineHeight: 1.42,
                    color: 'var(--ink)',
                    fontWeight: 400,
                    letterSpacing: '-0.005em',
                    animationDuration: '0.7s',
                  }}
                >
                  {renderAnswer(active.a)}
                </p>
              )}

              {/* Subtle placeholder lines during thinking */}
              {phase === 'thinking' && (
                <div className="space-y-2.5 pt-1" aria-hidden>
                  <div
                    className="shimmer-line h-3 rounded-full"
                    style={{ width: '82%' }}
                  />
                  <div
                    className="shimmer-line h-3 rounded-full"
                    style={{ width: '64%', animationDelay: '0.25s' }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Scroll hint */}
        <div
          className="mt-16 md:mt-20 flex items-center justify-center gap-2 fade-up"
          style={{ animationDelay: '0.48s' }}
        >
          <ArrowDown size={13} strokeWidth={1.75} style={{ color: 'var(--forest)' }} />
          <a
            href="#work"
            className="mono text-[11px] uppercase tracking-[0.2em] link-line"
            style={{ color: 'var(--forest)' }}
          >
            Selected work
          </a>
        </div>
      </div>
    </section>
  );
}

function SirinMark() {
  return (
    <svg
      width="44"
      height="44"
      viewBox="0 0 44 44"
      fill="none"
      aria-hidden
      className="shrink-0"
      style={{ transform: 'translateY(2px)' }}
    >
      <circle cx="22" cy="22" r="3.2" fill="var(--terracotta)" />
      <circle cx="22" cy="22" r="8.5" stroke="var(--forest)" strokeWidth="1" opacity="0.7" />
      <path
        d="M 6 22 a 16 16 0 0 1 32 0"
        stroke="var(--forest)"
        strokeWidth="1"
        opacity="0.4"
        strokeLinecap="round"
      />
      <path
        d="M 1 22 a 21 21 0 0 1 42 0"
        stroke="var(--moss)"
        strokeWidth="0.75"
        opacity="0.28"
        strokeLinecap="round"
      />
    </svg>
  );
}

function WaveIndicator({ active }) {
  const bars = [0, 1, 2, 3];
  return (
    <div className="flex items-end gap-[2px] h-3.5" aria-hidden>
      {bars.map((b) => (
        <span
          key={b}
          className="wave-bar"
          style={{
            width: 2,
            height: 10,
            background: 'var(--cream)',
            borderRadius: 2,
            animationDelay: `${b * (active ? 0.08 : 0.16)}s`,
            animationDuration: active ? '0.7s' : '1.6s',
            opacity: active ? 1 : 0.85,
          }}
        />
      ))}
    </div>
  );
}

function ThinkingDots() {
  return (
    <span className="inline-flex items-center gap-[3px] ml-0.5" aria-hidden>
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="dot-bob inline-block rounded-full"
          style={{
            width: 4,
            height: 4,
            background: 'var(--forest)',
            animationDelay: `${i * 0.16}s`,
          }}
        />
      ))}
    </span>
  );
}

function renderAnswer(text) {
  const accents = [
    'things I once needed',
    'feel less alone',
    'code at 2am',
    'stay with the problem until it works',
    'who I am from what I build',
    '100,000 young people',
    '200k downloads',
  ];
  let nodes = [text];
  accents.forEach((phrase, idx) => {
    nodes = nodes.flatMap((node, ni) => {
      if (typeof node !== 'string' || !node.includes(phrase)) return [node];
      const parts = node.split(phrase);
      const out = [];
      parts.forEach((part, pi) => {
        out.push(part);
        if (pi < parts.length - 1) {
          out.push(
            <em
              key={`em-${idx}-${ni}-${pi}`}
              className="serif"
              style={{ fontStyle: 'italic', color: 'var(--forest)', fontWeight: 400 }}
            >
              {phrase}
            </em>
          );
        }
      });
      return out;
    });
  });
  return nodes;
}
